import React, { useState, useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import PropTypes from "prop-types";
import {
  Modal,
  Box,
  TextField,
  Button,
  Typography,
  CircularProgress,
  Snackbar,
  Alert,
  IconButton,
  Checkbox,
  Chip,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
  Pagination,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import SearchIcon from "@mui/icons-material/Search";
import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";
import {
  updateCustomer,
  clearCustomerMessages,
  getCustomerOrgAccounts,
  resetCustomerOrgAccounts,
} from "../../../store/customer/customerSlice";
import {
  getCustomerAccountNumbers,
  getCustomerDisplayName,
  isCustomerActive,
  mergeOrgAccountsWithCustomer,
} from "../utils/customerHelpers";

const ACCOUNTS_ROWS_PER_PAGE = 10;

const EditCustomerAccountsModal = ({ open, onClose, customer, onSuccess }) => {
  const dispatch = useDispatch();
  const {
    isUpdatingCustomer,
    updateCustomerSuccess,
    updateCustomerError,
    isFetchingCustomerOrgAccounts,
    customerOrgAccountsSuccess,
    customerOrgAccountsError,
  } = useSelector((state) => state.customer);

  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("info");
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);

  const [selectedAccounts, setSelectedAccounts] = useState([]);
  const [initialAccountNumbers, setInitialAccountNumbers] = useState([]);
  const [hasRetrievedOrgAccounts, setHasRetrievedOrgAccounts] = useState(false);
  const [accountSearch, setAccountSearch] = useState("");
  const [accountFilter, setAccountFilter] = useState("ALL");
  const [accountsPage, setAccountsPage] = useState(0);

  const customerName = getCustomerDisplayName(customer);

  const apiOrgAccounts = useMemo(() => {
    const data = customerOrgAccountsSuccess?.data;
    if (!data || typeof data !== "object") return [];

    return Object.values(data)
      .filter(Boolean)
      .map((acc) => ({
        accountNumber: String(acc.account_number ?? ""),
        company: acc.company ?? "",
        status: String(acc.status ?? ""),
      }))
      .filter((acc) => acc.accountNumber);
  }, [customerOrgAccountsSuccess]);

  const customerOrgAccounts = useMemo(
    () => mergeOrgAccountsWithCustomer(apiOrgAccounts, customer),
    [apiOrgAccounts, customer]
  );

  const customerAccountNumbers = useMemo(
    () => (customer ? getCustomerAccountNumbers(customer) : []),
    [customer]
  );

  const normalizeStatus = (status) => String(status || "").toUpperCase();
  const isOnboarded = (acc) => normalizeStatus(acc.status) === "ONBOARDED";
  const isShadow = (acc) => normalizeStatus(acc.status) === "SHADOW";
  const hasCompany = (acc) => Boolean((acc.company || "").trim());

  const belongsToThisCustomer = (acc) => {
    if (customerAccountNumbers.includes(acc.accountNumber)) return true;
    const name = (customer?.customer_name || "").trim().toLowerCase();
    if (!name) return false;
    return (
      isShadow(acc) &&
      hasCompany(acc) &&
      acc.company.trim().toLowerCase() === name
    );
  };

  const isLockedAccount = (acc) => belongsToThisCustomer(acc);

  // Only unassigned accounts can be added; existing customer accounts stay checked and locked
  const isAddableAccount = (acc) =>
    isShadow(acc) && !hasCompany(acc) && !isLockedAccount(acc);

  const lockedAccountNumbers = useMemo(
    () =>
      customerOrgAccounts
        .filter(isLockedAccount)
        .map((acc) => acc.accountNumber),
    [customerOrgAccounts, customerAccountNumbers, customer?.customer_name]
  );

  const accountCounts = useMemo(() => {
    const counts = { onboarded: 0, customer: 0, unassigned: 0 };
    customerOrgAccounts.forEach((acc) => {
      if (isOnboarded(acc)) counts.onboarded += 1;
      else if (isShadow(acc) && hasCompany(acc)) counts.customer += 1;
      else if (isShadow(acc) && !hasCompany(acc)) counts.unassigned += 1;
    });
    return counts;
  }, [customerOrgAccounts]);

  const visibleAccounts = useMemo(() => {
    const q = accountSearch.trim().toLowerCase();
    return customerOrgAccounts.filter((acc) => {
      if (accountFilter === "ONBOARDED" && !isOnboarded(acc)) return false;
      if (accountFilter === "CUSTOMER" && !(isShadow(acc) && hasCompany(acc))) return false;
      if (accountFilter === "UNASSIGNED" && !(isShadow(acc) && !hasCompany(acc))) return false;

      if (!q) return true;
      return (
        acc.accountNumber.toLowerCase().includes(q) ||
        (acc.company || "").toLowerCase().includes(q)
      );
    });
  }, [customerOrgAccounts, accountSearch, accountFilter]);

  const visibleAddableAccountNumbers = useMemo(
    () => visibleAccounts.filter(isAddableAccount).map((a) => a.accountNumber),
    [visibleAccounts, customerAccountNumbers, customer?.customer_name]
  );

  const totalVisibleAccounts = visibleAccounts.length;
  const totalAccountPages = Math.max(1, Math.ceil(totalVisibleAccounts / ACCOUNTS_ROWS_PER_PAGE));

  const pagedVisibleAccounts = useMemo(() => {
    const start = accountsPage * ACCOUNTS_ROWS_PER_PAGE;
    return visibleAccounts.slice(start, start + ACCOUNTS_ROWS_PER_PAGE);
  }, [visibleAccounts, accountsPage]);

  useEffect(() => {
    setAccountsPage(0);
  }, [accountSearch, accountFilter, customerOrgAccountsSuccess]);

  const getFilterChipSx = (isActive) => ({
    height: 32,
    borderRadius: "999px",
    fontWeight: 700,
    fontSize: "0.75rem",
    cursor: "pointer",
    transition: "all 120ms ease",
    ...(isActive
      ? {
          backgroundColor: "#0f172a",
          color: "#ffffff",
          borderColor: "#0f172a",
          "& .MuiChip-icon": { color: "#ffffff" },
          "&:hover": { backgroundColor: "#111827" },
        }
      : {
          backgroundColor: "#ffffff",
          color: "#334155",
          borderColor: "#e2e8f0",
          "& .MuiChip-icon": { color: "#64748b" },
          "&:hover": { backgroundColor: "#f8fafc" },
        }),
  });

  const renderFilterLabel = (label, count) => `${label} (${count})`;

  const toggleAccountFilter = (filterKey) => {
    setAccountFilter((prev) => (prev === filterKey ? "ALL" : filterKey));
  };

  const handleRetrieveOrganization = () => {
    setHasRetrievedOrgAccounts(true);
    setAccountSearch("");
    setAccountFilter("ALL");
    setAccountsPage(0);
    dispatch(getCustomerOrgAccounts());
  };

  const handleAccountSelection = (accountNumber, isSelected) => {
    if (lockedAccountNumbers.includes(accountNumber)) return;

    if (isSelected) {
      setSelectedAccounts((prev) =>
        prev.includes(accountNumber) ? prev : [...prev, accountNumber]
      );
    } else {
      setSelectedAccounts((prev) => prev.filter((n) => n !== accountNumber));
    }
  };

  const handleSelectAll = () => {
    const allAddableSelected =
      visibleAddableAccountNumbers.length > 0 &&
      visibleAddableAccountNumbers.every((n) => selectedAccounts.includes(n));

    if (allAddableSelected) {
      setSelectedAccounts([...lockedAccountNumbers]);
    } else {
      setSelectedAccounts([
        ...new Set([...lockedAccountNumbers, ...visibleAddableAccountNumbers]),
      ]);
    }
  };

  const resetState = () => {
    setSelectedAccounts([]);
    setInitialAccountNumbers([]);
    setHasRetrievedOrgAccounts(false);
    setAccountSearch("");
    setAccountFilter("ALL");
    setAccountsPage(0);
    setConfirmDialogOpen(false);
  };

  const hasAccountChanges = useMemo(() => {
    const initialSet = new Set(initialAccountNumbers);
    const currentSet = new Set(selectedAccounts);
    if (initialSet.size !== currentSet.size) return true;
    for (const accountNumber of currentSet) {
      if (!initialSet.has(accountNumber)) return true;
    }
    return false;
  }, [initialAccountNumbers, selectedAccounts]);

  const handleSave = () => {
    if (!customer?.orgcode || selectedAccounts.length === 0 || !hasAccountChanges) return;
    setConfirmDialogOpen(true);
  };

  const handleConfirmSubmit = () => {
    if (!customer?.orgcode) return;

    dispatch(
      updateCustomer({
        orgcode: customer.orgcode,
        active: isCustomerActive(customer),
        accountNumbers: selectedAccounts,
      })
    );
    setConfirmDialogOpen(false);
  };

  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
    setSnackbarMessage("");
    setSnackbarSeverity("info");
    dispatch(clearCustomerMessages());
  };

  useEffect(() => {
    if (!open || lockedAccountNumbers.length === 0) return;
    setSelectedAccounts((prev) => [...new Set([...lockedAccountNumbers, ...prev])]);
    setInitialAccountNumbers((prev) => [...new Set([...prev, ...lockedAccountNumbers])]);
  }, [open, lockedAccountNumbers]);

  useEffect(() => {
    if (!open || !customer) return undefined;

    dispatch(clearCustomerMessages());
    const baseline = getCustomerAccountNumbers(customer);
    setInitialAccountNumbers(baseline);
    setSelectedAccounts(baseline);
    setHasRetrievedOrgAccounts(true);
    setAccountSearch("");
    setAccountFilter("ALL");
    setAccountsPage(0);
    setConfirmDialogOpen(false);
    dispatch(getCustomerOrgAccounts());

    return () => {
      dispatch(clearCustomerMessages());
      dispatch(resetCustomerOrgAccounts());
    };
  }, [open, customer?.orgcode, dispatch]);

  useEffect(() => {
    if (updateCustomerSuccess) {
      setSnackbarMessage(
        updateCustomerSuccess.message || "Customer accounts updated successfully."
      );
      setSnackbarSeverity("success");
      setSnackbarOpen(true);
      onSuccess?.();
      onClose();
      resetState();
      dispatch(clearCustomerMessages());
    }
  }, [updateCustomerSuccess, onClose, onSuccess, dispatch]);

  useEffect(() => {
    if (updateCustomerError) {
      setSnackbarMessage(updateCustomerError);
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
    }
  }, [updateCustomerError]);

  useEffect(() => {
    if (customerOrgAccountsError) {
      setSnackbarMessage(customerOrgAccountsError);
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
    }
  }, [customerOrgAccountsError]);

  const isSaveDisabled =
    !hasAccountChanges ||
    selectedAccounts.length === 0 ||
    isUpdatingCustomer ||
    isFetchingCustomerOrgAccounts;

  return (
    <>
      <Modal
        open={open && !!customer}
        onClose={(event, reason) => {
          if (reason === "backdropClick" || reason === "escapeKeyDown") return;
          onClose();
        }}
        aria-labelledby="edit-customer-accounts-modal"
      >
        <Paper
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: { xs: "95%", sm: 720 },
            maxWidth: 720,
            bgcolor: "background.paper",
            borderRadius: "8px",
            boxShadow: "0 8px 32px rgba(0, 0, 0, 0.3)",
            border: "1px solid #e2e8f0",
            display: "flex",
            flexDirection: "column",
            maxHeight: "90vh",
            overflow: "hidden",
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              backgroundColor: "#F5F5F5",
              padding: "16px 24px",
            }}
          >
            <Box>
              <Typography variant="h6" sx={{ fontWeight: "bold", color: "#000", fontSize: "1.125rem" }}>
                Edit Customer Accounts
              </Typography>
              <Typography variant="body2" sx={{ color: "#64748b", fontSize: "0.8125rem", mt: 0.25 }}>
                {customerName}
              </Typography>
            </Box>
            <IconButton
              onClick={() => {
                resetState();
                onClose();
              }}
              sx={{ color: "#000" }}
            >
              <CloseIcon />
            </IconButton>
          </Box>

          <Box sx={{ p: 3, overflowY: "auto", flex: 1 }}>
            <Box display="flex" justifyContent="center" mb={2}>
              <Button
                variant="outlined"
                color="primary"
                onClick={handleRetrieveOrganization}
                disabled={isFetchingCustomerOrgAccounts}
                startIcon={isFetchingCustomerOrgAccounts ? <CircularProgress size={18} /> : null}
                sx={{
                  textTransform: "none",
                  fontSize: "0.875rem",
                  fontWeight: 600,
                  borderRadius: "8px",
                }}
              >
                {isFetchingCustomerOrgAccounts
                  ? "Refreshing accounts..."
                  : "Refresh Organization Accounts"}
              </Button>
            </Box>

            {hasRetrievedOrgAccounts && (
              <Box
                sx={{
                  p: 2.5,
                  border: "1px solid #e2e8f0",
                  borderRadius: "8px",
                  backgroundColor: "#f8fafc",
                }}
              >
                <Alert
                  severity="info"
                  sx={{
                    mb: 2,
                    fontSize: "0.8125rem",
                    "& .MuiAlert-message": { width: "100%" },
                  }}
                >
                  Add unassigned AWS accounts to this customer. Accounts already linked to this
                  customer cannot be removed.
                </Alert>

                <Paper
                  variant="outlined"
                  sx={{ mb: 2, p: 1.5, borderColor: "#e2e8f0", borderRadius: "12px" }}
                >
                  <Box display="flex" alignItems="center" flexWrap="wrap" gap={1}>
                    <TextField
                      size="small"
                      value={accountSearch}
                      onChange={(e) => setAccountSearch(e.target.value)}
                      placeholder="Search accounts"
                      InputProps={{
                        startAdornment: <SearchIcon sx={{ color: "#94a3b8", mr: 0.5, fontSize: 18 }} />,
                      }}
                      sx={{
                        width: { xs: "100%", sm: 200 },
                        "& .MuiOutlinedInput-root": { fontSize: "0.875rem", borderRadius: "999px" },
                      }}
                    />
                    <Chip
                      clickable
                      icon={accountFilter === "ONBOARDED" ? <RemoveIcon /> : <AddIcon />}
                      label={renderFilterLabel("Paired", accountCounts.onboarded)}
                      variant="outlined"
                      onClick={() => toggleAccountFilter("ONBOARDED")}
                      sx={getFilterChipSx(accountFilter === "ONBOARDED")}
                    />
                    <Chip
                      clickable
                      icon={accountFilter === "CUSTOMER" ? <RemoveIcon /> : <AddIcon />}
                      label={renderFilterLabel("Customer", accountCounts.customer)}
                      variant="outlined"
                      onClick={() => toggleAccountFilter("CUSTOMER")}
                      sx={getFilterChipSx(accountFilter === "CUSTOMER")}
                    />
                    <Chip
                      clickable
                      icon={accountFilter === "UNASSIGNED" ? <RemoveIcon /> : <AddIcon />}
                      label={renderFilterLabel("Unassigned", accountCounts.unassigned)}
                      variant="outlined"
                      onClick={() => toggleAccountFilter("UNASSIGNED")}
                      sx={getFilterChipSx(accountFilter === "UNASSIGNED")}
                    />
                  </Box>
                </Paper>

                <TableContainer
                  component={Paper}
                  variant="outlined"
                  sx={{ borderColor: "#e2e8f0", borderRadius: "8px", maxHeight: 400 }}
                >
                  <Table stickyHeader size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ width: 48, backgroundColor: "#f8fafc" }}>
                          <Checkbox
                            size="small"
                            onChange={handleSelectAll}
                            checked={
                              visibleAddableAccountNumbers.length > 0 &&
                              visibleAddableAccountNumbers.every((n) =>
                                selectedAccounts.includes(n)
                              )
                            }
                            indeterminate={
                              visibleAddableAccountNumbers.length > 0 &&
                              visibleAddableAccountNumbers.some((n) =>
                                selectedAccounts.includes(n)
                              ) &&
                              !visibleAddableAccountNumbers.every((n) =>
                                selectedAccounts.includes(n)
                              )
                            }
                            disabled={visibleAddableAccountNumbers.length === 0}
                          />
                        </TableCell>
                        <TableCell sx={{ backgroundColor: "#f8fafc", fontWeight: 700 }}>
                          Account Number
                        </TableCell>
                        <TableCell sx={{ backgroundColor: "#f8fafc", fontWeight: 700 }}>
                          Status
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {pagedVisibleAccounts.map((acc) => {
                        const locked = isLockedAccount(acc);
                        const addable = isAddableAccount(acc);
                        const checked =
                          locked || selectedAccounts.includes(acc.accountNumber);

                        return (
                          <TableRow key={acc.accountNumber} hover>
                            <TableCell>
                              <Checkbox
                                size="small"
                                checked={checked}
                                disabled={locked || !addable}
                                onChange={(e) =>
                                  handleAccountSelection(acc.accountNumber, e.target.checked)
                                }
                              />
                            </TableCell>
                            <TableCell>
                              <Typography
                                sx={{
                                  fontSize: "0.75rem",
                                  fontFamily: "monospace",
                                  color: "#64748b",
                                }}
                              >
                                {acc.accountNumber}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              {isOnboarded(acc) && (
                                <Chip label={hasCompany(acc) ? acc.company : "PAIRED"} size="small" />
                              )}
                              {isShadow(acc) && hasCompany(acc) && (
                                <Chip label={acc.company} size="small" variant="outlined" />
                              )}
                              {isShadow(acc) && !hasCompany(acc) && (
                                <Chip label="UNASSIGNED" size="small" variant="outlined" />
                              )}
                            </TableCell>
                          </TableRow>
                        );
                      })}
                      {totalVisibleAccounts === 0 && (
                        <TableRow>
                          <TableCell colSpan={3}>
                            <Typography sx={{ color: "#64748b", fontSize: "0.875rem" }}>
                              {isFetchingCustomerOrgAccounts
                                ? "Loading accounts..."
                                : "No accounts match your search or filters."}
                            </Typography>
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>

                {totalVisibleAccounts > 0 && totalAccountPages > 1 && (
                  <Box display="flex" justifyContent="flex-end" mt={1.5}>
                    <Pagination
                      size="small"
                      count={totalAccountPages}
                      page={accountsPage + 1}
                      onChange={(_, p) => setAccountsPage(p - 1)}
                    />
                  </Box>
                )}

                {selectedAccounts.length > 0 && (
                  <Typography sx={{ mt: 2, fontSize: "0.8125rem", color: "#047857", fontWeight: 600 }}>
                    {selectedAccounts.length} account{selectedAccounts.length !== 1 ? "s" : ""}{" "}
                    selected
                  </Typography>
                )}
              </Box>
            )}

            <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 2, mt: 3 }}>
              <Button
                variant="outlined"
                onClick={() => {
                  resetState();
                  onClose();
                }}
                disabled={isUpdatingCustomer}
                sx={{ textTransform: "none", borderRadius: "8px", fontWeight: 600 }}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                color="primary"
                onClick={handleSave}
                disabled={isSaveDisabled}
                startIcon={isUpdatingCustomer ? <CircularProgress size={18} /> : null}
                sx={{ textTransform: "none", borderRadius: "8px", fontWeight: 600 }}
              >
                {isUpdatingCustomer ? "Saving..." : "Save Accounts"}
              </Button>
            </Box>
          </Box>
        </Paper>
      </Modal>

      <Dialog open={confirmDialogOpen} onClose={() => setConfirmDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 600 }}>Confirm Account Changes</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            You are adding accounts for <strong>{customerName}</strong>. Existing linked
            accounts will remain assigned.
          </DialogContentText>
          <Alert severity="warning">
            {selectedAccounts.length} account{selectedAccounts.length !== 1 ? "s" : ""} will be
            linked to this customer.
          </Alert>
        </DialogContent>
        <DialogActions sx={{ p: 2, gap: 1 }}>
          <Button onClick={() => setConfirmDialogOpen(false)} variant="outlined" sx={{ textTransform: "none" }}>
            Cancel
          </Button>
          <Button
            onClick={handleConfirmSubmit}
            variant="contained"
            disabled={isUpdatingCustomer}
            startIcon={isUpdatingCustomer ? <CircularProgress size={18} /> : null}
            sx={{ textTransform: "none" }}
          >
            {isUpdatingCustomer ? "Saving..." : "Confirm"}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
      >
        <Alert onClose={handleSnackbarClose} severity={snackbarSeverity} sx={{ width: "100%" }}>
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </>
  );
};

EditCustomerAccountsModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  customer: PropTypes.object,
  onSuccess: PropTypes.func.isRequired,
};

export default EditCustomerAccountsModal;
